import type { ColumnFilter } from "@tanstack/react-table";
import { CandidateQualification, DesignatedZoneType, displayQualification, displayZone } from "../../api/models/EntityCandidate";
import type { DateFilterValue } from "./DateFilter";
import type { TextFilterValue } from "./TextFilter";

export interface FilterDescription {
    label: string;
    value: string;
}

function formatTextFilterDescription(val: TextFilterValue | string | undefined): string {
    if (!val) return "";
    // Older/plain-string values are treated as a "contains" filter.
    if (typeof val === "string") return val;
    switch (val.mode) {
        case "empty":
            return "(empty)";
        case "notEmpty":
            return "(not empty)";
        case "startsWith":
            return val.value ? `Starts with "${val.value}"` : "";
        case "equals":
            return val.value ? `Equals "${val.value}"` : "";
        case "contains":
        default:
            return val.value ?? "";
    }
}

function formatDateFilterDescription(val: DateFilterValue | undefined): string {
    if (!val) return "";
    switch (val.mode) {
        case "on":
            return val.date ? `On ${val.date}` : "";
        case "gte":
            return val.date ? `From ${val.date}` : "";
        case "lte":
            return val.date ? `To ${val.date}` : "";
        case "range":
        default: {
            if (val.from && val.to) return `${val.from} – ${val.to}`;
            if (val.from) return `From ${val.from}`;
            if (val.to) return `To ${val.to}`;
            return "";
        }
    }
}

/** Builds a humanized description for a single column filter, e.g. used for export summaries and header tooltips. */
export function describeColumnFilter(filter: ColumnFilter, columnFilters: readonly ColumnFilter[]): FilterDescription | undefined {
    switch (filter.id) {
        case "lastName":
            return { label: "Last Name", value: formatTextFilterDescription(filter.value as TextFilterValue | string) };
        case "firstName":
            return { label: "First Name", value: formatTextFilterDescription(filter.value as TextFilterValue | string) };
        case "externalId":
            return { label: "External ID", value: formatTextFilterDescription(filter.value as TextFilterValue | string) };
        case "entityName":
            return { label: "Entity", value: formatTextFilterDescription(filter.value as TextFilterValue | string) };
        case "status": {
            const statuses = filter.value as string[];
            return { label: "Status", value: statuses.join(", ") };
        }
        case "designatedZoneType": {
            const zones = filter.value as string[];
            const zoneLabels = zones.map(z => {
                const zoneType = Object.values(DesignatedZoneType).find(v => v === z);
                return zoneType ? displayZone(zoneType) : z;
            });
            return { label: "Designated Zone", value: zoneLabels.join(", ") };
        }
        case "employmentHireDate": {
            const dateVal = filter.value as DateFilterValue;
            return { label: "Hire Date", value: formatDateFilterDescription(dateVal) };
        }
        case "questionnaireCompletedOn": {
            const dateVal = filter.value as DateFilterValue;
            return { label: "Questionnaire Completed On", value: formatDateFilterDescription(dateVal) };
        }
        case "qualifications": {
            const quals = filter.value as string[];
            const qualMode = columnFilters.find(qf => qf.id === "qualificationMode")?.value as string | undefined;
            const qualLabels = quals.map(q => displayQualification(q as CandidateQualification) ?? q);
            const mode = qualMode === "or" ? "OR" : "AND";
            return { label: "Qualifications", value: qualLabels.join(` ${mode} `) };
        }
        default:
            return undefined;
    }
}

/** Builds humanized filter descriptions for every entry in a ColumnFiltersState (excludes internal-only ids like qualificationMode). */
export function describeColumnFilters(columnFilters: readonly ColumnFilter[]): FilterDescription[] {
    const descriptions: FilterDescription[] = [];
    for (const f of columnFilters) {
        const desc = describeColumnFilter(f, columnFilters);
        if (desc) descriptions.push(desc);
    }
    return descriptions;
}
